import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import type { Workflow } from '@/schemas/workflow'
import { LLM_PROVIDER_VALUES, LLM_PROVIDER_INFO } from '@/schemas/llm-api-key'
import type { LLMProviderValue } from '@/schemas/llm-api-key'
import { useWorkflowStore, type DefaultLlmConfig } from '@/stores/workflow-store'
import { useLLMModelsStore } from '@/stores/llm-models-store'
import { workflowService } from '@/services/workflow'

interface DefaultLlmCardProps {
  workflow: Workflow
  projectId: string
}

/**
 * Phase 4 plan 04-05: Default LLM card inside the Workflow Settings tab.
 * Persists `Workflow.settings.default_llm` (D-05) without introducing a new
 * backend endpoint. Bulk-apply button (D-08) confirms impacted LLM nodes then
 * calls the store's `applyDefaultToAllLlmNodes` action.
 */
export function DefaultLlmCard({ workflow, projectId }: DefaultLlmCardProps) {
  const { t } = useTranslation('workflows')
  const setDefaultLlm = useWorkflowStore((s) => s.setDefaultLlm)
  const applyDefaultToAllLlmNodes = useWorkflowStore(
    (s) => s.applyDefaultToAllLlmNodes
  )
  const flowNodes = useWorkflowStore((s) => s.flowNodes)
  const updateWorkflow = useWorkflowStore((s) => s.updateWorkflow)

  const catalog = useLLMModelsStore((s) => s.catalog)
  const loadCatalog = useLLMModelsStore((s) => s.loadCatalog)

  const currentDefault =
    ((workflow.settings?.default_llm as Partial<DefaultLlmConfig> | undefined) ??
      {}) as Partial<DefaultLlmConfig>

  const provider = currentDefault.provider ?? null
  const model = currentDefault.model ?? null
  const temperature = currentDefault.temperature ?? 0.7
  const maxTokens = currentDefault.max_tokens ?? 2048

  const [saving, setSaving] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [applying, setApplying] = useState(false)

  useEffect(() => {
    if (provider) void loadCatalog(provider)
  }, [provider, loadCatalog])

  const llmNodes = useMemo(
    () => flowNodes.filter((n) => n.type === 'llm'),
    [flowNodes]
  )
  const llmNodeNames = useMemo(
    () =>
      llmNodes
        .map((n, i) => {
          const data = n.data as unknown as { label?: string }
          return data?.label?.trim() || `#${i + 1}`
        })
        .join(', '),
    [llmNodes]
  )

  const modelOptions = provider ? catalog[provider] ?? [] : []

  const handleSave = async () => {
    if (!provider || !model) {
      toast.error(t('detail.defaultLlm.saveError'))
      return
    }
    setSaving(true)
    try {
      await updateWorkflow(projectId, workflow.id, {
        settings: {
          ...workflow.settings,
          default_llm: {
            provider,
            model,
            temperature,
            max_tokens: maxTokens,
          },
        },
      })
      toast.success(t('detail.defaultLlm.saved'))
    } catch {
      toast.error(t('detail.defaultLlm.saveError'))
    } finally {
      setSaving(false)
    }
  }

  const handleApplyConfirm = async () => {
    setApplying(true)
    try {
      applyDefaultToAllLlmNodes()
      // Persist the updated nodes to the backend with new inheritance flags.
      const state = useWorkflowStore.getState()
      const serializedNodes = state.flowNodes.map((n) => {
        const data = n.data as unknown as Record<string, unknown>
        return { ...data, position: n.position }
      })
      await workflowService.update(projectId, workflow.id, {
        nodes: serializedNodes,
      })
      toast.success(
        t('detail.defaultLlm.applied', { count: llmNodes.length })
      )
      setConfirmOpen(false)
    } catch {
      toast.error(t('detail.defaultLlm.saveError'))
    } finally {
      setApplying(false)
    }
  }

  return (
    <Card variant="glass" className="p-6 mb-6">
      <div className="space-y-6 max-w-xl">
        <div>
          <h3 className="text-base font-semibold text-gray-100">
            {t('detail.defaultLlm.title')}
          </h3>
          <p className="text-sm text-gray-400 mt-1">
            {t('detail.defaultLlm.subtitle')}
          </p>
        </div>

        <div className="space-y-2">
          <Label className="text-gray-300">
            {t('detail.defaultLlm.providerLabel')}
          </Label>
          <Select
            value={provider ?? ''}
            onValueChange={(next) => {
              setDefaultLlm({
                provider: next as LLMProviderValue,
                // Clear model when provider changes (catalog invalidates).
                model: null,
              })
            }}
          >
            <SelectTrigger className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50">
              <SelectValue
                placeholder={t('detail.defaultLlm.providerPlaceholder')}
              />
            </SelectTrigger>
            <SelectContent>
              {LLM_PROVIDER_VALUES.map((p) => (
                <SelectItem key={p} value={p}>
                  {LLM_PROVIDER_INFO[p].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-gray-300">
            {t('detail.defaultLlm.modelLabel')}
          </Label>
          <Select
            value={model ?? ''}
            onValueChange={(next) => setDefaultLlm({ model: next })}
            disabled={!provider || modelOptions.length === 0}
          >
            <SelectTrigger className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50">
              <SelectValue
                placeholder={
                  !provider || modelOptions.length === 0
                    ? t('detail.defaultLlm.modelEmpty', {
                        provider: provider ?? '—',
                      })
                    : t('detail.defaultLlm.modelPlaceholder')
                }
              />
            </SelectTrigger>
            <SelectContent>
              {modelOptions.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  <span className="font-mono">{m.display_name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-gray-300 flex items-center justify-between">
            <span>{t('detail.defaultLlm.temperatureLabel')}</span>
            <span className="font-mono text-sm text-gray-400">
              {temperature.toFixed(2)}
            </span>
          </Label>
          <Slider
            min={0}
            max={2}
            step={0.1}
            value={[temperature]}
            onValueChange={(v) => setDefaultLlm({ temperature: v[0] })}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-gray-300">
            {t('detail.defaultLlm.maxTokensLabel')}
          </Label>
          <Input
            type="number"
            min={1}
            value={maxTokens}
            onChange={(e) =>
              setDefaultLlm({ max_tokens: Number(e.target.value) || 1 })
            }
            className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
          />
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button
            type="button"
            variant="action-indigo"
            disabled={saving || !provider || !model}
            onClick={handleSave}
          >
            {saving ? '...' : t('detail.defaultLlm.saveButton')}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirmOpen(true)}
            disabled={llmNodes.length === 0}
          >
            {t('detail.defaultLlm.applyToAll')}
          </Button>
        </div>
      </div>

      <ConfirmationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('detail.defaultLlm.applyConfirm.title')}
        description={
          llmNodes.length === 0
            ? t('detail.defaultLlm.applyConfirm.descriptionEmpty')
            : t('detail.defaultLlm.applyConfirm.description', {
                nodes: llmNodeNames,
              })
        }
        confirmLabel={t('detail.defaultLlm.applyConfirm.confirm')}
        confirmingLabel={t('detail.defaultLlm.applyConfirm.confirming')}
        isProcessing={applying}
        isDisabled={llmNodes.length === 0}
        onConfirm={handleApplyConfirm}
      />
    </Card>
  )
}
